import { Children, useLayoutEffect, useRef } from 'react';
import { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';

const ScrolledValueContainer = ({ children, selectProps }) => {
  const scrollRef = useRef(null);
  const childArray = Children.toArray(children);
  const hasValue = !!selectProps.value?.length;
  const values = hasValue ? childArray.slice(0, -1) : [];
  const input = childArray[childArray.length - 1];

  return (
    <div className='d-flex flex-column' style={{ flex: 1, minWidth: 0, height: 176 }}>
      <div className='interaction-contacts-select__input-row'>
        <div
          className={`interaction-contacts-select__input-inner d-flex align-items-center gap-2 ${
            selectProps.menuIsOpen ? 'hour-select-opendropdown' : ''
          }`}
        >
          <span
            className='flex-shrink-0 user-select-none text-main-blue-35 title-h3 text-center d-inline-block'
            style={{ minWidth: 36, transform: 'translateY(-1px)' }}
          >
            +
          </span>
          <div className='d-flex align-items-center position-relative' style={{ flex: 1, minWidth: 0 }}>
            {!selectProps.inputValue && (
              <span className='interaction-contacts-select__placeholder-text position-absolute'>
                {selectProps.placeholder}
              </span>
            )}
            {input}
          </div>
          {selectProps.cornerAction}
        </div>
      </div>
      <CustomScrollbar
        ref={scrollRef}
        className='simple-scrollbar'
        style={{ flex: 1, minHeight: 0 }}
        maximalThumbYSize={50}
        minimalThumbYSize={50}
        disabled={selectProps.menuIsOpen}
      >
        <div
          className='d-flex flex-column align-items-stretch'
          style={{ gap: 2, padding: hasValue ? '0 8px 4px 8px' : 0 }}
        >
          {values}
        </div>
      </CustomScrollbar>
    </div>
  );
};

const ContactsMenuList = ({ children, selectProps, ...props }) => {
  const { RenderActionsBar } = selectProps;
  const [heightContainer, containerRef] = useHeightContainer();
  const autoHeightMax = Math.max(120, Math.min(300, heightContainer - 16));

  // Selecting an option re-renders this list with a fresh `children` array every time, which
  // otherwise loses scroll position (nothing else preserves it across that update) - very annoying
  // when picking several people from a long, scrolled-down list.
  const scrollbarRef = useRef(null);
  const lastScrollTop = useRef(0);

  useLayoutEffect(() => {
    if (lastScrollTop.current) {
      scrollbarRef.current?.scrollTop(lastScrollTop.current);
    }
  }, [children]);

  return (
    <components.MenuList {...props} selectProps={selectProps}>
      <div ref={containerRef}>
        <CustomScrollbar
          ref={scrollbarRef}
          className='simple-scrollbar top-spaced-scrollbar-medium'
          autoHeight
          autoHeightMax={autoHeightMax}
          minimalThumbYSize={50}
          maximalThumbYSize={75}
          onScroll={(target) => {
            lastScrollTop.current = target.scrollTop;
          }}
        >
          {children}
        </CustomScrollbar>
      </div>
      {RenderActionsBar?.()}
    </components.MenuList>
  );
};

// react-select's MultiValueRemove falls back to its own CrossIcon svg only when given no children,
// so passing our own icon here is enough to swap it without losing the built-in click/aria handling.
const MultiValueRemove = (props) => (
  <components.MultiValueRemove {...props}>
    <span className='icon-close text-light-blue' />
  </components.MultiValueRemove>
);

const colourStyles = (disabled, selectRef) => ({
  control: (styles) => ({
    ...styles,
    borderColor: 'transparent',
    outline: 'none',
    boxShadow: 'none',
    ':hover': { borderColor: 'transparent' },
    backgroundColor: 'transparent',
    cursor: 'text',
    minHeight: '40px',
  }),
  option: (styles) => ({
    ...styles,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: '#00122b',
    fontSize: '16px',
    ':active': { ...styles[':active'], backgroundColor: 'transparent' },
  }),
  placeholder: (styles) => ({ ...styles, color: 'rgba(0,18,43,0.6)' }),
  input: (styles) => ({ ...styles, margin: 0, padding: 0 }),
  indicatorsContainer: (styles) => ({ ...styles, backgroundColor: 'transparent' }),
  multiValue: (styles) => ({
    ...styles,
    backgroundColor: 'transparent',
    borderRadius: 16,
    width: 'calc(100% - 4px)',
    margin: '2px',
    pointerEvents: disabled ? 'none' : 'all',
    ':hover': { backgroundColor: 'rgba(0, 148, 204, 0.04)' },
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    padding: '2px 6px',
    fontSize: 14,
    color: '#00122b',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    flex: 1,
    minWidth: 0,
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    borderRadius: '0 16px 16px 0',
    paddingLeft: 6,
    paddingRight: 6,
    marginRight: 4,
    color: '#0094cc',
    ':hover': { backgroundColor: 'rgba(0, 148, 204, 0.08)', color: '#0094cc' },
  }),
  menuList: (styles) => ({ ...styles, padding: 0, maxHeight: 'none', overflowY: 'hidden' }),
  menu: (styles) => ({
    ...styles,
    margin: 0,
    background: '#fff',
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(60, 63, 66, 0.12), 0 4px 10px rgba(60, 63, 66, 0.05)',
    border: '1px solid rgba(212, 212, 212, 0.6)',
  }),
  menuPortal: (styles, { rect }) => {
    // react-select anchors to the whole control (the 176px value-list box); we want it right
    // under the search row instead, so it overlaps the value-list like a suggestions dropdown.
    // Measure the real row instead of guessing its height, so this can't drift out of sync with the CSS.
    const inputRow = selectRef.current?.controlRef?.querySelector('.interaction-contacts-select__input-row');
    const inputRowHeight = inputRow?.getBoundingClientRect().height ?? 46;
    return {
      ...styles,
      zIndex: 100001, // above .react-modal-overlay's z-index: 100000, since this menu is a document.body sibling of the modal
      top: rect.top + inputRowHeight,
      left: rect.left + 8,
      width: rect.width - 16,
    };
  },
  loadingIndicator: (styles) => ({ ...styles, position: 'absolute', top: '12px', right: '12px' }),
});

const ContactsSelect = ({
  loadOptions,
  value,
  onChange,
  placeholder,
  disabled = false,
  defaultOptions = false,
  selectRef,
  MultiValueLabel: CustomMultiValueLabel,
  Option: CustomOption,
  GroupHeading: CustomGroupHeading,
  noOptionsMessage = ({ inputValue: iv }) => (iv?.length > 0 ? 'No results' : null),
  menuIsOpen,
  ...rest
}) => (
  <div className='interaction-contacts-select border-general border-grey position-relative flex-grow-1 w-100 mt-2'>
    <AsyncSelect
      ref={selectRef}
      isMulti
      isDisabled={disabled}
      defaultOptions={defaultOptions}
      cacheOptions={false}
      captureMenuScroll={false}
      menuShouldScrollIntoView={false}
      closeMenuOnSelect={false}
      openMenuOnClick={false}
      blurInputOnSelect={false}
      onSelectResetsInput={false}
      menuPortalTarget={document.body}
      menuPosition='fixed'
      loadOptions={loadOptions}
      value={value}
      onChange={onChange}
      menuIsOpen={menuIsOpen}
      placeholder={placeholder}
      classNamePrefix='react-custom-select-contacts'
      styles={colourStyles(disabled, selectRef)}
      hideSelectedOptions={false}
      noOptionsMessage={noOptionsMessage}
      components={{
        ...(CustomMultiValueLabel && { MultiValueLabel: CustomMultiValueLabel }),
        ValueContainer: ScrolledValueContainer,
        MenuList: ContactsMenuList,
        MultiValueRemove,
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
        ClearIndicator: () => null,
        ...(CustomOption && { Option: CustomOption }),
        ...(CustomGroupHeading && { GroupHeading: CustomGroupHeading }),
      }}
      {...rest}
    />
  </div>
);

export default ContactsSelect;
